import { Text, View } from 'react-native';
import { createFileRoute } from '@tanstack/react-router';
import { LegalScreen, H2, P, Bullet } from '@/components/LegalScreen';

export const Route = createFileRoute('/privacy')({
  component: PrivacyScreen,
});

function B({ children }: { children: React.ReactNode }) {
  return <Text style={{ fontWeight: '600', color: '#18181B' }}>{children}</Text>;
}

function PrivacyScreen() {
  return (
    <LegalScreen title="Privacy Policy" effective="15 May 2026">
      <P>
        halvn ("we", "us", "our") is an Australian project. This Privacy Policy explains what
        personal information we collect when you use the halvn application, how we use it, who
        we share it with, and the choices you have.
      </P>
      <P>
        By using halvn you agree to the practices described here. If you don't agree, please
        don't create an account or use the service.
      </P>

      <H2>1. Who we are</H2>
      <P>
        halvn is operated from Sydney, NSW, Australia. You can reach us about privacy at
        privacy@halvn.app.
      </P>

      <H2>2. Information we collect</H2>
      <P>We collect only what we need to run the service:</P>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>Account data:</B> the email address you use to receive sign-in codes. We don't
          collect or store passwords.
        </Text>
      </Bullet>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>Health data you enter:</B> the compounds you track, concentration, schedule, and
          individual dose log entries.
        </Text>
      </Bullet>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>Device data:</B> basic technical signals (browser, app version, network status).
        </Text>
      </Bullet>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>Diagnostics:</B> crash and sync errors with dose values, compound names and emails
          scrubbed before they're sent.
        </Text>
      </Bullet>

      <H2>3. How we use it</H2>
      <Bullet>To provide the core service — calculate, display, and sync your dose data.</Bullet>
      <Bullet>To authenticate you via one-time email codes.</Bullet>
      <Bullet>To respond when you contact support.</Bullet>
      <Bullet>To monitor reliability, detect abuse, and improve the product.</Bullet>
      <P>
        We do not use your health data to train AI models, do not sell it, and do not share it
        with advertisers.
      </P>

      <H2>4. Who we share it with</H2>
      <P>
        Limited third-party processors handle data on our behalf under confidentiality
        obligations:
      </P>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>InstantDB</B> — database and real-time sync, US infrastructure.
        </Text>
      </Bullet>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>Email delivery</B> — sends one-time codes; sees email + code only.
        </Text>
      </Bullet>
      <Bullet>
        <Text className="text-sm leading-relaxed text-textDim">
          <B>Hosting + CDN</B> — Cloudflare; sees standard request data, not dose records.
        </Text>
      </Bullet>

      <H2>5. Where your data is stored</H2>
      <P>
        Your account and dose records are stored on InstantDB infrastructure in the United
        States. By using halvn you consent to this transfer.
      </P>

      <H2>6. How long we keep it</H2>
      <P>
        We keep your data while your account is active. If you delete your account we remove
        personal data from active systems within 30 days. Backups roll off after 60 days.
      </P>

      <H2>7. Your rights</H2>
      <Bullet>Access the personal data we hold.</Bullet>
      <Bullet>Correct inaccurate data — most fields are editable in-app.</Bullet>
      <Bullet>Delete your account and have your data erased.</Bullet>
      <Bullet>Export your data in a machine-readable format.</Bullet>
      <Bullet>Object to specific processing or withdraw consent.</Bullet>
      <Bullet>Lodge a complaint with the Office of the Australian Information Commissioner.</Bullet>
      <P>Email privacy@halvn.app.</P>

      <H2>8. Security</H2>
      <P>
        TLS for data in transit, encryption at rest via InstantDB, two-factor on production
        access. If a breach affects your data we will notify you under the Australian
        Notifiable Data Breaches scheme.
      </P>

      <H2>9. Children</H2>
      <P>halvn is for adults aged 18 and over. We don't knowingly collect data from minors.</P>

      <H2>10. Analytics</H2>
      <P>
        Privacy-focused analytics with no dose values, compound names, or emails in events. Opt
        out from Settings.
      </P>

      <H2>11. Changes</H2>
      <P>
        We may update this policy. Material changes will be communicated in-app or by email
        before they take effect.
      </P>

      <H2>12. Contact</H2>
      <P>Privacy: privacy@halvn.app. Support: hello@halvn.app.</P>

      <View className="h-8" />
    </LegalScreen>
  );
}
